import api from "@/libs/axios";
import { QueryConfig } from "@/libs/query";
import { useQuery } from "@tanstack/react-query";
import { apiEnum } from "@/pages/taskpane/shared/types/enums/apiEnum";
import { QUERY_KEY } from "@/pages/taskpane/shared/constants/queryKey";

// Types for Timeline API
export interface TimelineMeeting {
  id: number;
  from: string; // ISO format with timezone (e.g., "2024-11-14T09:00:00+07:00")
  to: string; // ISO format with timezone
  subject: string;
  status: string; // "Confirmed" | "Tentative"
  organizerEmail: string;
  preBufferTime: number; // in minutes
  postBufferTime: number; // in minutes
}

export interface TimelineData {
  [resourceId: string]: {
    [date: string]: TimelineMeeting[]; // date format: "2024-11-14"
  };
}

export interface TimelineResponse {
  status: number;
  message: string;
  data: TimelineData; // Resource IDs are directly in data, not wrapped in timeLine
}

type PayLoadType = {
  resourceIds: number[];
  referenceDate: string; // .NET ticks format (e.g., 638361024000000000)
  organizerEmail: string;
  timeZoneStandardName: string; // e.g., "SE Asia Standard Time"
};

/**
 * Convert JavaScript Date to .NET ticks
 * .NET ticks = number of 100-nanosecond intervals since 01/01/0001 00:00:00
 * JavaScript timestamp = milliseconds since 01/01/1970 00:00:00
 * 
 * Formula: .NET ticks = (JS timestamp in ms * 10000) + 621355968000000000
 * where 621355968000000000 is the ticks between 01/01/0001 and 01/01/1970
 */
export const dateToNetTicks = (date: Date | string): number => {
  const dateObj = typeof date === "string" ? new Date(date) : date;
  const jsTimestamp = dateObj.getTime(); // milliseconds since 1970-01-01
  const ticksPerMillisecond = 10000;
  const ticksBetween1970And0001 = 621355968000000000;
  return jsTimestamp * ticksPerMillisecond + ticksBetween1970And0001;
};

/**
 * Fetch timeline data for multiple resources
 */
const getTimeline = async (payload: PayLoadType): Promise<TimelineResponse> => {
  const { data } = await api.post(apiEnum.resourcesTimeline, payload);
  return data;
};

/**
 * Query options for timeline
 */
export const options = (payload: PayLoadType) => ({
  queryKey: [
    QUERY_KEY.GET_RESOURCES,
    "timeline",
    {
      resourceIds: payload.resourceIds.sort().join(","),
      referenceDate: payload.referenceDate,
      organizerEmail: payload.organizerEmail,
      timeZone: payload.timeZoneStandardName,
    },
  ],
  queryFn: () => {
    // console.log("🔄 Fetching timeline - Params:", {
    //   resourceIds: payload.resourceIds,
    //   referenceDate: payload.referenceDate,
    //   organizerEmail: payload.organizerEmail,
    //   timeZone: payload.timeZoneStandardName,
    // });
    return getTimeline(payload);
  },
  // Cache strategy
  staleTime: 2 * 60 * 1000, // Cache fresh for 2 minutes
  gcTime: 5 * 60 * 1000, // Keep in cache for 5 minutes after unmount
});

type GetTimelineType = {
  queryConfig?: QueryConfig<typeof options>;
  payload: PayLoadType;
  enabled?: boolean;
};

/**
 * Hook to fetch timeline data for resources
 * 
 * @example
 * ```tsx
 * const { data, isLoading, error } = useGetTimeline({
 *   payload: {
 *     resourceIds: [101, 102],
 *     referenceDate: dateToNetTicks(new Date()),
 *     organizerEmail: "user@example.com",
 *     timeZoneStandardName: "SE Asia Standard Time"
 *   },
 *   enabled: true // optional: control when query runs
 * });
 * 
 * // Access timeline data
 * const resourceTimeline = data?.data?.timeLine?.[101];
 * const meetingsOnDate = resourceTimeline?.["2024-11-14"] || [];
 * ```
 */
export const useGetTimeline = ({ queryConfig, payload, enabled = true }: GetTimelineType) => {
  const query = useQuery({
    ...options(payload),
    ...queryConfig,
    enabled: enabled,
  });

  // Debug: Log query status
  // console.log("📦 Timeline query status:", {
  //   status: query.status,
  //   isFetching: query.isFetching,
  //   dataUpdatedAt: query.dataUpdatedAt ? new Date(query.dataUpdatedAt).toLocaleTimeString() : "never",
  //   resourceIds: payload.resourceIds,
  // });

  return query;
};

/**
 * Helper function to get meetings for a specific resource and date
 */
export const getMeetingsForResourceAndDate = (
  timelineData: TimelineData | undefined,
  resourceId: number,
  date: string // format: "2024-11-14"
): TimelineMeeting[] => {
  if (!timelineData) return [];
  const resourceTimeline = timelineData[resourceId.toString()];
  if (!resourceTimeline) return [];
  return resourceTimeline[date] || [];
};

/**
 * Helper function to format date to "YYYY-MM-DD" for timeline lookup
 */
// export const formatDateForTimeline = (date: Date | string): string => {
//   const dateObj = typeof date === "string" ? new Date(date) : date;
//   const year = dateObj.getFullYear();
//   const month = String(dateObj.getMonth() + 1).padStart(2, "0");
//   const day = String(dateObj.getDate()).padStart(2, "0");
//   return `${year}-${month}-${day}`;
// };
export const formatDateForTimeline = (date: Date | string): string => {
  if (typeof date === "string") {
    const dateMatch = date.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (dateMatch) {
      return `${dateMatch[1]}-${dateMatch[2]}-${dateMatch[3]}`;
    }
    const dateObj = new Date(date);
    const year = dateObj.getFullYear();
    const month = String(dateObj.getMonth() + 1).padStart(2, "0");
    const day = String(dateObj.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }

  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, "0");
  const day = String(date.getUTCDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

